/**
 * GA4 Event Tracking – Lavlah.mn ЭЕШ Funnel
 *
 * Dynamic gtag.js loader + sessionStorage-based deduplication.
 * Only loads GA when VITE_GA4_MEASUREMENT_ID is set (safe for local dev).
 *
 * Primary API: trackFunnelEvent(name, params?)
 * Backward-compat: gtagEvent(name, params?)
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID ?? '';
export const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID ?? '';

// ── Dynamic GA4 script injection ──
if (GA_MEASUREMENT_ID && typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);

  // Configure Google Ads tag on the same gtag.js instance
  if (GOOGLE_ADS_ID) {
    window.gtag('config', GOOGLE_ADS_ID);
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

// ── Dedup helpers ──

/** Once-per-session guard (returns true if already fired) */
function sessionFired(key: string): boolean {
  try {
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, '1');
    return false;
  } catch {
    return false; // SSR / privacy mode fallback
  }
}

/** Transaction-based dedup for custom purchase events like purchase_weekly (localStorage) */
function txFired(txId: string): boolean {
  const key = `ga_purchase_${txId}`;
  try {
    if (localStorage.getItem(key)) {
      console.log('[GA4] custom purchase deduped:', txId);
      return true;
    }
    localStorage.setItem(key, '1');
    return false;
  } catch {
    return false;
  }
}

/** Separate dedup for standard ecommerce purchase event (different prefix to avoid blocking custom events) */
function ecomTxFired(txId: string): boolean {
  const key = `ga_ecom_${txId}`;
  try {
    if (localStorage.getItem(key)) {
      console.log('[GA4] ecommerce purchase deduped:', txId);
      return true;
    }
    localStorage.setItem(key, '1');
    return false;
  } catch {
    return false;
  }
}

// Events that should only fire once per session
const ONCE_PER_SESSION = new Set([
  'view_content',
  'start_diagnostic',
  'begin_checkout',
]);

// ── Public API ──

/**
 * Fire a funnel event with auto-injected `funnel: 'eesh_2026'`.
 * Handles per-session and per-transaction deduplication.
 */
export function trackFunnelEvent(
  eventName: string,
  params: Record<string, unknown> = {},
) {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || !window.gtag) return;

  // Dedup: purchase events by transaction_id
  if ((eventName === 'purchase' || eventName.startsWith('purchase_')) && params.transaction_id) {
    if (txFired(String(params.transaction_id))) return;
  }
  // Dedup: start_trial per plan_type per session
  else if (eventName === 'start_trial') {
    const key = `ga_evt_start_trial_${params.plan_type ?? ''}`;
    if (sessionFired(key)) return;
  }
  // Dedup: once-per-session events
  else if (ONCE_PER_SESSION.has(eventName)) {
    if (sessionFired(`ga_evt_${eventName}`)) {
      console.log('[GA4] event deduped (session):', eventName);
      return;
    }
  }
  // share_result: no dedup (fire every click)

  console.log('[GA4] event fired:', eventName, params);
  window.gtag('event', eventName, { funnel: 'eesh_2026', ...params });
}

/**
 * Backward-compatible wrapper. Non-funnel callers still work.
 */
export function gtagEvent(name: string, params?: Record<string, unknown>) {
  trackFunnelEvent(name, params);
}

/**
 * Placeholder for subscribe_renewed.
 * Call this from a server-side webhook handler when renewal is confirmed.
 * TODO: Wire into subscription renewal webhook edge function.
 */
export function trackSubscriptionRenewed(planType: string, transactionId: string) {
  trackFunnelEvent('subscribe_renewed', {
    plan_type: planType,
    transaction_id: transactionId,
  });
}

/**
 * Fire a standard GA4 ecommerce `purchase` event with items[] array.
 * Compatible with Google Ads Smart Bidding when imported as a conversion.
 */
/** MNT → USD approximate rate. Update periodically or move to env/config. */
const MNT_TO_USD = 1 / 3450;

export function trackEcommercePurchase(params: {
  transaction_id: string;
  value: number;       // amount in MNT
  currency: string;    // kept for backward compat but overridden to USD
  item_id: string;
  item_name: string;
  plan_type: string;
}) {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || !window.gtag) return;
  if (ecomTxFired(params.transaction_id)) return;

  const valueUsd = Math.round(params.value * MNT_TO_USD * 100) / 100; // 2 decimal places

  console.log('[GA4] ecommerce purchase event fired:', params.transaction_id, `$${valueUsd}`, `(${params.value} MNT)`);
  window.gtag('event', 'purchase', {
    transaction_id: params.transaction_id,
    value: valueUsd,
    currency: 'USD',
    items: [{
      item_id: params.item_id,
      item_name: params.item_name,
      item_category: 'Subscription',
      price: valueUsd,
      quantity: 1,
    }],
    funnel: 'eesh_2026',
    plan_type: params.plan_type,
    value_mnt: params.value,
    local_currency: 'MNT',
  });
}

/**
 * Fire a GA4 refund event. Ready for future use with admin panel or webhook refund flows.
 */
export function trackRefund(params: {
  transaction_id: string;
  value: number;
  currency: string;
}) {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'refund', {
    transaction_id: params.transaction_id,
    value: params.value,
    currency: params.currency,
  });
}

/**
 * Fire a Google Ads conversion event directly.
 * Use when you have a conversion label from the Google Ads UI.
 */
export function trackConversion(conversionLabel: string, params: Record<string, unknown> = {}) {
  if (!GOOGLE_ADS_ID || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
    ...params,
  });
}
